import React from 'react';
import Footer from '../components/Footer';
import Nav from '../components/Nav';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Nav />

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16 flex-1">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-normal mb-3 sm:mb-4 leading-tight" style={{ fontFamily: 'Instrument Serif, serif' }}>
          Privacy Policy
        </h1>
        <p className="text-sm text-[#1a1a1a]/60 mb-10 sm:mb-12">Effective Date: January 1, 2026</p>
        
        <div className="prose prose-base sm:prose-lg max-w-none space-y-6 sm:space-y-8">
          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              1. Introduction
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed">
              Verilock ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our compliance guidance platform at verilock.ai.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              2. Information We Collect
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed mb-4">
              We collect the following types of information:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#1a1a1a]/80">
              <li><strong>Contact Information:</strong> Your name and work email address</li>
              <li><strong>Company Information:</strong> Your company name and business details</li>
              <li><strong>Wizard Responses:</strong> Answers you provide in our compliance assessment wizard (21 questions about your business, data handling, security practices, and compliance goals)</li>
              <li><strong>Payment Information:</strong> Billing details processed through our payment provider, Paddle</li>
              <li><strong>Usage Data:</strong> Information about how you interact with our service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              3. How We Store Your Information
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed">
              Your data is securely stored in MongoDB databases. We implement appropriate technical and organizational measures to protect your information against unauthorized access, alteration, disclosure, or destruction.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              4. How We Use Your Information
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#1a1a1a]/80">
              <li>Generate personalized compliance roadmaps and policy documents</li>
              <li>Provide and improve our compliance guidance service</li>
              <li>Process your subscription payments</li>
              <li>Communicate with you about your account and our services</li>
              <li>Analyze usage patterns to improve product features</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              5. Third-Party Services
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed mb-4">
              We share your information with the following third-party service providers:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#1a1a1a]/80">
              <li><strong>Paddle:</strong> For payment processing. Your payment information is handled directly by Paddle and subject to their privacy policy.</li>
              <li><strong>Anthropic (Claude API):</strong> Your wizard responses and company information are processed through Claude to generate compliance content. This data is used only for generation and not stored by Anthropic.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              6. Your Rights
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed mb-4">
              Under GDPR and CCPA, you have the following rights:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#1a1a1a]/80">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data</li>
              <li><strong>Portability:</strong> Request transfer of your data to another service</li>
              <li><strong>Objection:</strong> Object to processing of your personal data</li>
              <li><strong>Restriction:</strong> Request restriction of processing</li>
            </ul>
            <p className="text-[#1a1a1a]/80 leading-relaxed mt-4">
              To exercise any of these rights, please contact us at hello@verilock.ai.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              7. Cookies and Tracking
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed">
              We use minimal, essential cookies to maintain your session and provide basic functionality. We do not use tracking cookies or third-party advertising cookies. We use PostHog for basic product analytics to understand how our service is used and improve user experience.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              8. Data Retention
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed">
              We retain your personal data for as long as your account is active or as needed to provide you services. If you request deletion of your data, we will delete it within 30 days, except where we are required to retain it for legal compliance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              9. International Data Transfers
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed">
              Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy and applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              10. Changes to This Policy
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the effective date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              11. Contact Us
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed">
              For any privacy-related questions or requests, please contact us at:{' '}
              <a href="mailto:hello@verilock.ai" className="text-[#0a5c44] hover:underline">
                hello@verilock.ai
              </a>
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Privacy;
